import {
  documentsApi,
  onboardingApi,
  profileApi,
  propertiesApi,
  remindersApi,
  rentalAgreementsApi,
  tenantsApi,
  type OnboardingCsvImportResultDto,
  type ProfileDto,
  type PropertyDto,
  type ReminderDto,
  type TenantDto,
} from '../../services/api';
import { ApiError } from '../../services/core.api';
import { getEuCountryLabel, renderEuCountryOptions, resolveEuCountryCode } from '../../services/eu-countries';
import { authService } from '../../services/auth';
import { billingApi } from '../../services/billing.api';
import { t } from '../../services/i18n';
import { markTrialNoticePending } from '../../services/trial-notice';
import './rc-onboarding-shell';
import { RcOnboardingShell } from './rc-onboarding-shell';
import { normalizeFormValue, STEPS } from './onboarding-shared';

type SubmitEventDetail = { form: HTMLFormElement };
type AccountTypeEventDetail = { isBusinessAccount: boolean };

const ONBOARDING_EVENTS = ['submit', 'prev', 'skip', 'csv-import', 'account-type-change', 'logout'] as const;

export class RcOnboardingWizard extends HTMLElement {
  private currentStep = 0;
  private profile: ProfileDto | null = null;
  private createdProperty: PropertyDto | null = null;
  private createdTenant: TenantDto | null = null;
  private createdTenantRentAmount: number | null = null;
  private createdTenantStartDate: string | null = null;
  private createdRentalAgreementId: string | null = null;
  private createdDocumentId: string | null = null;
  private createdDocumentName: string | null = null;
  private createdReminder: ReminderDto | null = null;
  private isLoading = false;
  private isImportingCsv = false;
  private csvImportNotice: string | null = null;
  private csvImportErrors: string[] = [];
  private tenantOnboardingError = '';

  private get isTenantMode(): boolean {
    return authService.currentUser?.accountMode === 'tenant';
  }

  async connectedCallback(): Promise<void> {
    ONBOARDING_EVENTS.forEach((eventName) => this.addEventListener(`onboarding-${eventName}`, this.handleOnboardingEvent as EventListener));
    this.addEventListener('click', this.handleClick);
    this.innerHTML = '<div class="onboarding-loading"><div class="spinner"></div></div>';
    try {
      const status = await onboardingApi.getStatus();
      if (status.isCompleted) {
        window.location.href = authService.resolvePostOnboardingRedirect(this.isTenantMode ? '/tenant/active/' : '/dashboard/');
        return;
      }
    } catch {
      // If check fails, show wizard anyway
    }

    if (this.isTenantMode) {
      this.profile = await profileApi.get().catch(() => null);
      this.render();
      return;
    }

    const [profileResult, propertiesResult, remindersResult] = await Promise.allSettled([
      profileApi.get(),
      propertiesApi.getAll(),
      remindersApi.getAll(),
    ]);

    this.profile = profileResult.status === 'fulfilled' ? profileResult.value : null;
    const properties = propertiesResult.status === 'fulfilled' ? propertiesResult.value : [];
    const reminders = remindersResult.status === 'fulfilled' ? remindersResult.value : [];

    if (properties.length > 0) {
      this.createdProperty = properties[0];

      try {
        const agreements = await rentalAgreementsApi.getByProperty(this.createdProperty.id);
        const agreement = agreements[0] ?? null;
        if (agreement) {
          const tenant = await tenantsApi.getById(agreement.tenantId);
          this.createdTenant = tenant;
          this.createdRentalAgreementId = agreement.id;
          this.createdTenantRentAmount = agreement.rentAmount ?? null;
          this.createdTenantStartDate = agreement.startDate ?? null;
        }
      } catch {}

      try {
        const tenantDocs = this.createdTenant ? await documentsApi.getByTenant(this.createdTenant.id) : [];
        const firstDoc = tenantDocs[0] ?? (await documentsApi.getByProperty(this.createdProperty.id))[0] ?? null;
        if (firstDoc) {
          this.createdDocumentId = firstDoc.id;
          this.createdDocumentName = firstDoc.fileName;
        }
      } catch {}
    }

    this.createdReminder = reminders[0] ?? null;
    this.currentStep = this.determineResumeStep();
    this.clearInterruptedCheckoutResume();
    this.render();
  }

  disconnectedCallback(): void {
    ONBOARDING_EVENTS.forEach((eventName) => this.removeEventListener(`onboarding-${eventName}`, this.handleOnboardingEvent as EventListener));
    this.removeEventListener('click', this.handleClick);
  }

  private handleClick = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    const action = target?.closest<HTMLElement>('[data-action]');
    if (action?.dataset.action === 'logout') {
      this.dispatchEvent(new CustomEvent('onboarding-logout', { bubbles: true }));
    }
  };

  private determineResumeStep(): number {
    const profileDone = !!(
      this.profile?.firstName &&
      this.profile?.lastName &&
      this.profile?.street &&
      this.profile?.houseNumber &&
      this.profile?.postalCode &&
      this.profile?.city &&
      this.profile?.country
    );
    if (!profileDone) return 0;
    if (!this.createdProperty) return 1;
    if (!this.createdTenant) return 2;
    if (!this.createdDocumentId) return 3;
    return 4;
  }

  private clearInterruptedCheckoutResume(): void {
    if (this.isTenantMode || this.currentStep === 0) {
      return;
    }

    if (authService.peekPostOnboardingRedirect('/dashboard/') !== '/pricing/?checkout=resume') {
      return;
    }

    authService.rememberPostOnboardingRedirect('');
    billingApi.clearPendingCheckout();
  }

  private get totalSteps(): number {
    return STEPS.length;
  }

  private get progressPct(): number {
    return Math.round((this.currentStep / this.totalSteps) * 100);
  }

  private get completionDestination(): 'dashboard' | 'checkout' {
    if (this.isTenantMode) {
      return 'dashboard';
    }

    const redirectTarget = authService.peekPostOnboardingRedirect('/dashboard/');
    return redirectTarget === '/pricing/?checkout=resume' ? 'checkout' : 'dashboard';
  }

  private render(): void {
    if (this.isTenantMode) {
      this.renderTenantOnboarding();
      return;
    }

    this.innerHTML = '<rc-onboarding-shell></rc-onboarding-shell>';
    const shell = this.querySelector('rc-onboarding-shell') as RcOnboardingShell | null;
    if (!shell) return;
    shell.currentStep = this.currentStep;
    shell.totalSteps = this.totalSteps;
    shell.progressPct = this.progressPct;
    shell.profile = this.profile;
    shell.createdProperty = this.createdProperty;
    shell.createdTenant = this.createdTenant;
    shell.createdTenantRentAmount = this.createdTenantRentAmount;
    shell.createdTenantStartDate = this.createdTenantStartDate;
    shell.createdDocumentId = this.createdDocumentId;
    shell.createdDocumentName = this.createdDocumentName;
    shell.createdReminder = this.createdReminder;
    shell.csvImportNotice = this.csvImportNotice;
    shell.csvImportErrors = this.csvImportErrors;
    shell.isImportingCsv = this.isImportingCsv;
    shell.currentUserName = authService.currentUser?.name || '';
    shell.completionDestination = this.completionDestination;
    shell.render();
  }

  private renderTenantOnboarding(): void {
    const profile = this.profile ?? {
      email: authService.currentUser?.email || '',
      displayName: authService.currentUser?.name || '',
      providerDisplayName: authService.currentUser?.name || '',
      customDisplayName: null,
      accountMode: 'tenant' as const,
      allowedCountryCodes: [],
      firstName: '',
      lastName: '',
      isBusinessAccount: false,
      companyName: null,
      vatNumber: null,
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: '',
      billingStreet: null,
      billingHouseNumber: null,
      billingPostalCode: null,
      billingCity: null,
      billingCountry: null,
    };
    const tenantProgressLabel = t('onboarding.progressLabel', { completed: 1, total: 1 });
    const tenantStepBadge = t('onboarding.stepBadge', { current: 1, total: 1 });
    const saveLabel = this.isLoading ? t('common.saving') : t('tenantOnboarding.saveAndContinue');

    this.innerHTML = `
      <div class="wizard-shell wizard-shell--tenant">
        <aside class="wizard-sidebar wizard-sidebar--tenant">
          <div class="wizard-brand">Rentomax</div>
          <div class="wizard-sidebar-title">${t('pages.onboarding.title')}</div>
          <p class="wizard-sidebar-sub">${t('tenantOnboarding.intro')}</p>
          <div class="tenant-wizard-sidebar-card">
            <div class="tenant-wizard-sidebar-progress">
              <span class="tenant-wizard-sidebar-progress-value">100%</span>
              <span class="tenant-wizard-sidebar-progress-label">${tenantProgressLabel}</span>
            </div>
            <div class="tenant-wizard-sidebar-fields">
              <span class="tenant-wizard-sidebar-chip">${t('tenantOnboarding.firstName')}</span>
              <span class="tenant-wizard-sidebar-chip">${t('tenantOnboarding.lastName')}</span>
              <span class="tenant-wizard-sidebar-chip">${t('profile.street')}</span>
              <span class="tenant-wizard-sidebar-chip">${t('profile.city')}</span>
              <span class="tenant-wizard-sidebar-chip">${t('profile.country')}</span>
            </div>
          </div>
        </aside>

        <div class="wizard-main wizard-main--tenant">
          <div class="wizard-header">
            <div class="wizard-step-badge">${tenantStepBadge}</div>
            <button type="button" class="btn-logout wizard-logout-btn" data-action="logout">${t('auth.logout')}</button>
          </div>
          <div class="wizard-content wizard-content--tenant">
            <div class="tenant-onboarding-panel">
              <div class="tenant-onboarding-intro">
                <div class="tenant-onboarding-intro-copy">
                  <h1 class="wizard-step-title">${t('pages.onboarding.title')}</h1>
                  <p class="wizard-step-desc">${t('tenantOnboarding.intro')}</p>
                </div>
                <div class="tenant-onboarding-accent">
                  <span class="tenant-onboarding-accent-orb tenant-onboarding-accent-orb--primary"></span>
                  <span class="tenant-onboarding-accent-orb tenant-onboarding-accent-orb--secondary"></span>
                </div>
              </div>

              <form id="tenant-onboarding-form" class="tenant-onboarding-form profile-address-form" novalidate>
                ${this.tenantOnboardingError ? `<div class="wizard-error tenant-onboarding-error">${this.tenantOnboardingError}</div>` : ''}
                <div class="tenant-onboarding-section">
                  <div class="tenant-onboarding-section-heading">
                    <span class="tenant-onboarding-section-index">01</span>
                    <div>
                      <strong>${t('tenantOnboarding.firstName')} & ${t('tenantOnboarding.lastName')}</strong>
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label for="tenant-first-name">${t('tenantOnboarding.firstName')}</label>
                      <input id="tenant-first-name" name="firstName" type="text" value="${profile.firstName || ''}" autocomplete="given-name">
                    </div>
                    <div class="form-group">
                      <label for="tenant-last-name">${t('tenantOnboarding.lastName')}</label>
                      <input id="tenant-last-name" name="lastName" type="text" value="${profile.lastName || ''}" autocomplete="family-name">
                    </div>
                  </div>
                </div>

                <div class="tenant-onboarding-section">
                  <div class="tenant-onboarding-section-heading">
                    <span class="tenant-onboarding-section-index">02</span>
                    <div>
                      <strong>${t('profile.street')} & ${t('profile.city')}</strong>
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group">
                      <label for="tenant-street">${t('profile.street')}</label>
                      <input id="tenant-street" name="street" type="text" value="${profile.street || ''}" autocomplete="address-line1">
                    </div>
                    <div class="form-group profile-house-number-group">
                      <label for="tenant-house-number">${t('profile.houseNumber')}</label>
                      <input id="tenant-house-number" name="houseNumber" type="text" value="${profile.houseNumber || ''}" autocomplete="address-line2">
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group profile-postal-code-group">
                      <label for="tenant-postal-code">${t('profile.postalCode')}</label>
                      <input id="tenant-postal-code" name="postalCode" type="text" value="${profile.postalCode || ''}" autocomplete="postal-code">
                    </div>
                    <div class="form-group">
                      <label for="tenant-city">${t('profile.city')}</label>
                      <input id="tenant-city" name="city" type="text" value="${profile.city || ''}" autocomplete="address-level2">
                    </div>
                  </div>
                  <div class="form-group">
                    <label for="tenant-country">${t('profile.country')}</label>
                    <input id="tenant-country" name="country" type="text" list="tenant-country-options" value="${getEuCountryLabel(profile.country, profile.allowedCountryCodes)}" autocomplete="country-name" placeholder="${t('profile.countryPlaceholder')}">
                    <datalist id="tenant-country-options">${renderEuCountryOptions(profile.country, profile.allowedCountryCodes)}</datalist>
                  </div>
                </div>

                <div class="wizard-actions tenant-onboarding-actions">
                  <div class="tenant-onboarding-actions-note">${tenantProgressLabel}</div>
                  <div class="wizard-actions-right">
                    <button type="submit" class="btn btn-primary btn-lg wizard-next-btn" ${this.isLoading ? 'disabled' : ''}>${saveLabel}</button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    `;

    this.querySelector<HTMLFormElement>('#tenant-onboarding-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.handleTenantOnboardingSubmit(event.currentTarget as HTMLFormElement);
    });
  }

  private handleOnboardingEvent = async (event: Event): Promise<void> => {
    switch ((event as CustomEvent).type) {
      case 'onboarding-prev':
        if (this.currentStep > 0) {
          this.currentStep -= 1;
          this.render();
        }
        return;
      case 'onboarding-skip':
        await this.advanceStep();
        return;
      case 'onboarding-csv-import':
        await this.handleCsvImport();
        return;
      case 'onboarding-submit':
        await this.handleSubmit((event as CustomEvent<SubmitEventDetail>).detail.form);
        return;
      case 'onboarding-account-type-change':
        this.syncBusinessFlag((event as CustomEvent<AccountTypeEventDetail>).detail?.isBusinessAccount === true);
        return;
      case 'onboarding-logout':
        authService.logout();
        return;
      default:
        return;
    }
  };

  private syncBusinessFlag(isBusinessAccount: boolean): void {
    if (!this.profile) {
      this.profile = {
        email: '',
        displayName: authService.currentUser?.name || '',
        providerDisplayName: authService.currentUser?.name || '',
        customDisplayName: null,
        allowedCountryCodes: [],
        isBusinessAccount,
        companyName: null,
        vatNumber: null,
        street: '',
        houseNumber: '',
        postalCode: '',
        city: '',
        country: '',
        billingStreet: null,
        billingHouseNumber: null,
        billingPostalCode: null,
        billingCity: null,
        billingCountry: null,
      };
      return;
    }

    this.profile = {
      ...this.profile,
      isBusinessAccount,
      companyName: isBusinessAccount ? this.profile.companyName : null,
      vatNumber: isBusinessAccount ? this.profile.vatNumber : null,
      billingStreet: isBusinessAccount ? this.profile.billingStreet : null,
      billingHouseNumber: isBusinessAccount ? this.profile.billingHouseNumber : null,
      billingPostalCode: isBusinessAccount ? this.profile.billingPostalCode : null,
      billingCity: isBusinessAccount ? this.profile.billingCity : null,
      billingCountry: isBusinessAccount ? this.profile.billingCountry : null,
    };
  }

  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    const button = this.querySelector<HTMLButtonElement>('.wizard-next-btn');
    if (button) {
      button.disabled = loading;
      button.classList.toggle('is-loading', loading);
    }
  }

  private setCsvImportLoading(loading: boolean): void {
    this.isImportingCsv = loading;
    const button = this.querySelector<HTMLButtonElement>('#wizard-import-csv');
    if (button) {
      button.disabled = loading;
      button.classList.toggle('is-loading', loading);
    }
  }

  private clearError(): void {
    const errorEl = this.querySelector('#wizard-error') as HTMLElement | null;
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.style.display = 'none';
    }
  }

  private showError(message: string): void {
    const errorEl = this.querySelector('#wizard-error') as HTMLElement | null;
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }
  }

  private showInputValidation(form: HTMLFormElement, selector: string, message: string): void {
    const input = form.querySelector<HTMLInputElement>(selector);
    if (!input) {
      this.showError(message);
      this.setLoading(false);
      return;
    }

    input.setCustomValidity(message);
    input.reportValidity();
    this.setLoading(false);
  }

  private isDisallowedCountry(rawValue: FormDataEntryValue | null, allowedCountryCodes?: readonly string[] | null): boolean {
    return normalizeFormValue(rawValue) !== null && !resolveEuCountryCode((rawValue as string) || '', allowedCountryCodes);
  }

  private async handleCsvImport(): Promise<void> {
    if (this.isImportingCsv) return;
    const fileInput = this.querySelector<HTMLInputElement>('#w-csv-file');
    const file = fileInput?.files?.[0];
    if (!file) {
      this.csvImportNotice = null;
      this.csvImportErrors = [t('onboarding.csvImportFileRequired')];
      this.render();
      return;
    }

    this.setCsvImportLoading(true);
    this.csvImportNotice = null;
    this.csvImportErrors = [];

    try {
      const result = await onboardingApi.importCsv({ content: await file.text() });
      await this.hydrateImportedState(result);
      this.csvImportNotice = t('onboarding.csvImportSuccess', {
        propertyCount: result.createdPropertyCount,
        tenantCount: result.createdTenantCount,
      });
      this.csvImportErrors = [];
      this.currentStep = 3;
      this.render();
    } catch (error: unknown) {
      this.csvImportNotice = null;
      this.csvImportErrors = this.extractCsvImportErrors(error);
      this.render();
    } finally {
      this.setCsvImportLoading(false);
    }
  }

  private extractCsvImportErrors(error: unknown): string[] {
    if (error instanceof ApiError && Array.isArray(error.details?.errors) && error.details.errors.length > 0) {
      return error.details.errors.map((entry: { rowNumber?: number; message?: string }) =>
        entry.rowNumber
          ? t('onboarding.csvImportRowError', {
              rowNumber: entry.rowNumber,
              message: entry.message || t('onboarding.csvImportInvalidData'),
            })
          : (entry.message || t('onboarding.csvImportInvalidData')),
      );
    }
    if (error instanceof Error && error.message) {
      return [error.message];
    }
    return [t('onboarding.csvImportError')];
  }

  private async hydrateImportedState(result: OnboardingCsvImportResultDto): Promise<void> {
    const propertyId = result.firstPropertyId;
    const tenantId = result.firstTenantId;

    this.createdProperty = propertyId ? await propertiesApi.getById(propertyId) : null;
    this.createdTenant = tenantId ? await tenantsApi.getById(tenantId) : null;

    if (this.createdProperty) {
      const agreements = await rentalAgreementsApi.getByProperty(this.createdProperty.id);
      const agreement = agreements.find((item) => !tenantId || item.tenantId === tenantId) ?? agreements[0] ?? null;
      this.createdRentalAgreementId = agreement?.id ?? null;
      this.createdTenantRentAmount = agreement?.rentAmount ?? null;
      this.createdTenantStartDate = agreement?.startDate ?? null;
    } else {
      this.createdRentalAgreementId = null;
      this.createdTenantRentAmount = null;
      this.createdTenantStartDate = null;
    }
  }

  private async handleSubmit(form: HTMLFormElement): Promise<void> {
    if (this.isTenantMode) {
      await this.handleTenantOnboardingSubmit(form);
      return;
    }

    if (this.isLoading) return;
    const stepId = STEPS[this.currentStep].id;

    this.clearError();
    this.setLoading(true);
    try {
      switch (stepId) {
        case 'profile': await this.submitProfileStep(form); break;
        case 'property': await this.submitPropertyStep(form); break;
        case 'tenant': await this.submitTenantStep(form); break;
        case 'document': await this.submitDocumentStep(form); break;
        case 'reminder': await this.submitReminderStep(form); break;
      }
    } catch (error: any) {
      this.showError(error?.message || t('common.errorGeneric'));
      this.setLoading(false);
    }
  }

  private async handleTenantOnboardingSubmit(form: HTMLFormElement): Promise<void> {
    if (this.isLoading) return;

    const fd = new FormData(form);
    const firstName = normalizeFormValue(fd.get('firstName'));
    const lastName = normalizeFormValue(fd.get('lastName'));
    const street = normalizeFormValue(fd.get('street'));
    const houseNumber = normalizeFormValue(fd.get('houseNumber'));
    const postalCode = normalizeFormValue(fd.get('postalCode'));
    const city = normalizeFormValue(fd.get('city'));
    const countryInput = fd.get('country');
    const country = resolveEuCountryCode((countryInput as string) || '', this.profile?.allowedCountryCodes);

    if (!firstName || !lastName || !street || !houseNumber || !postalCode || !city || !country) {
      this.tenantOnboardingError = this.isDisallowedCountry(countryInput, this.profile?.allowedCountryCodes)
        ? t('onboarding.error.countryNotAllowed')
        : t('tenantOnboarding.requiredError');
      this.render();
      return;
    }

    this.isLoading = true;
    this.tenantOnboardingError = '';
    this.render();

    try {
      this.profile = await profileApi.update({
        customDisplayName: null,
        firstName,
        lastName,
        isBusinessAccount: false,
        companyName: null,
        vatNumber: null,
        street,
        houseNumber,
        postalCode,
        city,
        country,
        billingStreet: null,
        billingHouseNumber: null,
        billingPostalCode: null,
        billingCity: null,
        billingCountry: null,
      });
      authService.updateCurrentUser({
        name: this.profile.displayName,
        onboardingCompleted: true,
      });
      await onboardingApi.complete();
      window.location.href = authService.resolvePostOnboardingRedirect('/tenant/active/');
    } catch (error: any) {
      this.tenantOnboardingError = error?.message || t('tenantOnboarding.saveError');
      this.isLoading = false;
      this.render();
    }
  }

  private async submitProfileStep(form: HTMLFormElement): Promise<void> {
    const fd = new FormData(form);
    const isBusinessAccount = fd.get('accountType') === 'business';
    const companyName = normalizeFormValue(fd.get('companyName'));
    const vatNumber = normalizeFormValue(fd.get('vatNumber'));
    const firstName = normalizeFormValue(fd.get('firstName'));
    const lastName = normalizeFormValue(fd.get('lastName'));
    const street = ((fd.get('street') as string) || '').trim();
    const houseNumber = ((fd.get('houseNumber') as string) || '').trim();
    const postalCode = ((fd.get('postalCode') as string) || '').trim();
    const city = ((fd.get('city') as string) || '').trim();
    const countryInput = fd.get('country');
    const country = resolveEuCountryCode((countryInput as string) || '', this.profile?.allowedCountryCodes);

    form.querySelectorAll<HTMLInputElement>('#w-company-name, #w-vat-number').forEach((input) => {
      input.setCustomValidity('');
    });

    if (isBusinessAccount && !companyName) {
      this.showInputValidation(form, '#w-company-name', t('onboarding.error.companyNameRequired'));
      return;
    }

    if (isBusinessAccount && !vatNumber) {
      this.showInputValidation(form, '#w-vat-number', t('onboarding.error.vatNumberRequired'));
      return;
    }

    if (!firstName || !lastName) {
      this.showError(t('tenantOnboarding.requiredError'));
      this.setLoading(false);
      return;
    }

    if (!street || !houseNumber || !postalCode || !city || !country) {
      this.showError(
        this.isDisallowedCountry(countryInput, this.profile?.allowedCountryCodes)
          ? t('onboarding.error.countryNotAllowed')
          : t('onboarding.error.profileRequired'),
      );
      this.setLoading(false);
      return;
    }

    const billingCountry = isBusinessAccount
      ? resolveEuCountryCode((fd.get('billingCountry') as string) || '', this.profile?.allowedCountryCodes)
      : null;

    this.profile = await profileApi.update({
      customDisplayName: null,
      firstName,
      lastName,
      isBusinessAccount,
      companyName: isBusinessAccount ? companyName : null,
      vatNumber: isBusinessAccount ? vatNumber : null,
      street,
      houseNumber,
      postalCode,
      city,
      country,
      billingStreet: isBusinessAccount ? normalizeFormValue(fd.get('billingStreet')) : null,
      billingHouseNumber: isBusinessAccount ? normalizeFormValue(fd.get('billingHouseNumber')) : null,
      billingPostalCode: isBusinessAccount ? normalizeFormValue(fd.get('billingPostalCode')) : null,
      billingCity: isBusinessAccount ? normalizeFormValue(fd.get('billingCity')) : null,
      billingCountry,
    });
    authService.updateCurrentUserName(this.profile.displayName);
    await this.advanceStep();
  }

  private async submitPropertyStep(form: HTMLFormElement): Promise<void> {
    const fd = new FormData(form);
    const address = ((fd.get('address') as string) || '').trim();
    if (!address) {
      this.showError(t('onboarding.error.propertyAddressRequired'));
      this.setLoading(false);
      return;
    }

    const data = {
      address,
      type: fd.get('type') as string,
      structureType: 'Standalone' as const,
      status: 'Vacant' as const,
      description: (fd.get('description') as string) || undefined,
    };

    if (this.createdProperty) {
      this.createdProperty = await propertiesApi.update(this.createdProperty.id, data);
    } else {
      this.createdProperty = await propertiesApi.create(data);
    }
    await this.advanceStep();
  }

  private async submitTenantStep(form: HTMLFormElement): Promise<void> {
    if (!this.createdProperty) {
      await this.advanceStep();
      return;
    }

    const fd = new FormData(form);
    const firstName = ((fd.get('firstName') as string) || '').trim();
    const lastName = ((fd.get('lastName') as string) || '').trim();
    const rentAmount = parseFloat(fd.get('rentAmount') as string);
    const startDate = fd.get('startDate') as string;

    if (!firstName || !lastName || Number.isNaN(rentAmount) || !startDate) {
      this.showError(t('onboarding.error.tenantRequired'));
      this.setLoading(false);
      return;
    }

    const email = (fd.get('email') as string) || undefined;
    const phone = (fd.get('phone') as string) || undefined;

    if (this.createdTenant && this.createdRentalAgreementId) {
      this.createdTenant = await tenantsApi.update(this.createdTenant.id, { firstName, lastName, email, phone });
      await rentalAgreementsApi.update(this.createdRentalAgreementId, {
        tenantId: this.createdTenant.id,
        propertyId: this.createdProperty.id,
        rentAmount,
        startDate,
      });
    } else {
      this.createdTenant = await tenantsApi.create({
        propertyId: this.createdProperty.id,
        firstName,
        lastName,
        email,
        phone,
        rentAmount,
        startDate,
      });
      const agreement = await rentalAgreementsApi.create({
        tenantId: this.createdTenant.id,
        propertyId: this.createdProperty.id,
        rentAmount,
        startDate,
      });
      this.createdRentalAgreementId = agreement.id;
    }

    this.createdTenantRentAmount = rentAmount;
    this.createdTenantStartDate = startDate;
    await this.advanceStep();
  }

  private async submitDocumentStep(form: HTMLFormElement): Promise<void> {
    const fd = new FormData(form);
    const fileInput = form.querySelector<HTMLInputElement>('#w-file');
    const file = fileInput?.files?.[0];

    if (!file && !this.createdDocumentId) {
      this.showError(t('onboarding.error.documentFileRequired'));
      this.setLoading(false);
      return;
    }

    if (file) {
      if (this.createdDocumentId) {
        await documentsApi.delete(this.createdDocumentId);
        this.createdDocumentId = null;
        this.createdDocumentName = null;
      }

      const linkedId = this.createdTenant?.id
        ? { tenantId: this.createdTenant.id }
        : this.createdProperty?.id
          ? { propertyId: this.createdProperty.id }
          : {};

      const { documentId, uploadUrl } = await documentsApi.create({
        ...linkedId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSizeBytes: file.size,
        documentType: fd.get('documentType') as string,
        expiryDate: (fd.get('expiryDate') as string) || undefined,
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!uploadResponse.ok) {
        await documentsApi.delete(documentId).catch(() => undefined);
        throw new Error(t('document.uploadError'));
      }

      this.createdDocumentId = documentId;
      this.createdDocumentName = file.name;
    }

    await this.advanceStep();
  }

  private async submitReminderStep(form: HTMLFormElement): Promise<void> {
    const fd = new FormData(form);
    const title = ((fd.get('title') as string) || '').trim();
    const dueDate = fd.get('dueDate') as string;

    if (!title || !dueDate) {
      this.showError(t('onboarding.error.reminderRequired'));
      this.setLoading(false);
      return;
    }

    if (this.createdReminder) {
      this.createdReminder = await remindersApi.update(this.createdReminder.id, {
        title,
        dueDate,
        isCompleted: false,
      });
    } else {
      this.createdReminder = await remindersApi.create({
        title,
        dueDate,
        propertyId: this.createdProperty?.id,
      });
    }
    await this.finishAndRedirect();
  }

  private async advanceStep(): Promise<void> {
    this.isLoading = false;
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep += 1;
      this.render();
    } else {
      await this.finishAndRedirect();
    }
  }

  private async finishAndRedirect(): Promise<void> {
    try {
      await onboardingApi.complete();

      const redirectPath = authService.resolvePostOnboardingRedirect(this.isTenantMode ? '/tenant/active/' : '/dashboard/');
      const isCheckoutResume = !this.isTenantMode
        && redirectPath === '/pricing/?checkout=resume'
        && billingApi.getPendingCheckout() !== null;

      const overview = await billingApi.getOverview().catch(() => null);
      if (!overview?.subscription.hasManageableSubscription && !isCheckoutResume) {
        markTrialNoticePending();
      }

      authService.updateCurrentUser({ onboardingCompleted: true });
      window.location.href = redirectPath;
    } catch (error: any) {
      this.showError(error?.message || t('onboarding.error.complete'));
      this.setLoading(false);
    }
  }
}

if (!customElements.get('rc-onboarding-wizard')) {
  customElements.define('rc-onboarding-wizard', RcOnboardingWizard);
}
